import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Check, Loader2, Rocket, Phone } from "lucide-react";
import axios from "axios";
import confetti from "canvas-confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// pioggia di coriandoli nei colori del brand all'invio del preventivo
const fireConfetti = () => {
  const colors = ["#3565AE", "#F58220", "#FFD166", "#ffffff"];
  confetti({ particleCount: 130, spread: 80, origin: { y: 0.6 }, colors, zIndex: 9999 });
  setTimeout(() => confetti({ particleCount: 70, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors, zIndex: 9999 }), 250);
  setTimeout(() => confetti({ particleCount: 70, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors, zIndex: 9999 }), 450);
};

const PLAN_LABELS = {
  hero: "Consulenza Generale",
  nav: "Consulenza Generale",
  privati: "Privati",
  aziende: "Aziende e Villaggi",
  enti: "Enti e Strutture",
};

const SERVICE_OPTIONS = [
  "Pulizia Aree Verdi",
  "Pulizie Private Interni ed Esterni",
  "Progettazione Aree Verdi",
  "Gestione Dipendenti",
  "Trasporti, Sgomberi e Traslochi",
  "Altro / Non sono sicuro",
];

export default function EarlyAccessModal({ open, plan, onClose }) {
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setService("");
      setPhone("");
      setMessage("");
      setStatus("idle");
      setFeedback("");
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/early-access`, {
        email: email.trim(),
        plan,
        service,
        phone: phone.trim(),
        message: message.trim(),
      });
      setFeedback(
        data.duplicate
          ? "Abbiamo già una tua richiesta. Ti ricontatteremo presto!"
          : data.email_sent
            ? "Richiesta inviata! Controlla la tua email: ti abbiamo appena mandato la conferma."
            : "Richiesta inviata. Ti ricontatteremo entro 48 ore lavorative."
      );
      setStatus("success");
      fireConfetti();
    } catch (err) {
      setFeedback(err.response?.data?.detail || "Errore di connessione. Riprova tra poco.");
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="early-access-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#16345F]/60 p-4 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#D9E4F5] bg-white p-8 shadow-[0_30px_90px_rgba(22,52,95,0.35)]"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full"
              style={{ background: "radial-gradient(ellipse, rgba(53,101,174,0.15), transparent 70%)" }}
            />
            <button
              data-testid="early-access-close-button"
              onClick={onClose}
              aria-label="Chiudi"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg border border-[#D9E4F5] text-slate-400 transition-colors duration-300 hover:border-pulse hover:text-pulse"
            >
              <X className="h-4 w-4" />
            </button>

            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-neon/40 bg-neon/10">
              <Rocket className="h-6 w-6 text-neon" />
            </span>
            <h3 className="mt-5 font-display text-xl font-bold leading-snug sm:text-2xl">
              Richiedi un Preventivo
            </h3>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-pulse" data-testid="early-access-plan-label">
              Richiesta per: {PLAN_LABELS[plan] || plan}
            </p>

            {status === "success" ? (
              <div className="mt-6" data-testid="early-access-success">
                <div className="flex items-center gap-3 rounded-xl border border-volt/40 bg-volt/10 p-4">
                  <Check className="h-5 w-5 shrink-0 text-volt" />
                  <p className="text-sm text-[#16345F]">{feedback}</p>
                </div>
                <button
                  data-testid="early-access-done-button"
                  onClick={onClose}
                  className="mt-6 w-full rounded-full bg-neon py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:shadow-[0_12px_40px_rgba(53,101,174,0.5)]"
                >
                  Perfetto, ci sentiamo presto
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6">
                <label htmlFor="early-access-email" className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  La tua email
                </label>
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-[#D9E4F5] bg-[#F5F8FD] px-4 transition-colors duration-300 focus-within:border-neon/60">
                  <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                  <input
                    id="early-access-email"
                    data-testid="early-access-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@dominio.it"
                    className="w-full bg-transparent py-3.5 text-sm text-[#16345F] outline-none placeholder:text-slate-400"
                  />
                </div>
                <label htmlFor="early-access-service" className="mt-4 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Servizio desiderato (facoltativo)
                </label>
                <select
                  id="early-access-service"
                  data-testid="early-access-service-select"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className="mt-2 w-full appearance-none rounded-xl border border-[#D9E4F5] bg-[#F5F8FD] px-4 py-3.5 text-sm text-[#16345F] outline-none transition-colors duration-300 focus:border-neon/60"
                >
                  <option value="">Seleziona un servizio…</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <label htmlFor="early-access-phone" className="mt-4 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Telefono (facoltativo — ti richiamiamo noi)
                </label>
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-[#D9E4F5] bg-[#F5F8FD] px-4 transition-colors duration-300 focus-within:border-neon/60">
                  <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                  <input
                    id="early-access-phone"
                    data-testid="early-access-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="375 123 4567"
                    className="w-full bg-transparent py-3.5 text-sm text-[#16345F] outline-none placeholder:text-slate-400"
                  />
                </div>
                <label htmlFor="early-access-message" className="mt-4 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Messaggio (facoltativo)
                </label>
                <textarea
                  id="early-access-message"
                  data-testid="early-access-message-input"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Descrivici brevemente cosa ti serve…"
                  className="mt-2 w-full resize-none rounded-xl border border-[#D9E4F5] bg-[#F5F8FD] px-4 py-3 text-sm text-[#16345F] outline-none transition-colors duration-300 focus:border-neon/60 placeholder:text-slate-400"
                />
                {status === "error" && (
                  <p data-testid="early-access-error" className="mt-3 rounded-lg border border-pulse/40 bg-pulse/10 p-3 text-xs text-pulse">
                    {feedback}
                  </p>
                )}
                <button
                  data-testid="early-access-submit-button"
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-neon py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:shadow-[0_12px_40px_rgba(53,101,174,0.5)] disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Invio in corso…
                    </>
                  ) : (
                    "Invia la Richiesta"
                  )}
                </button>
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  Nessun impegno. Inviando accetti la{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-neon underline">
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
